import { useState } from 'react'
import { auroraStore, useAuroraStore } from '../../store/auroraStore'
import { authorName, type PlanBlock } from '../../store/selectors'
import { gameStatus, markLabel, materialsForBlock } from '@aurora/core'
import { countThings, isPathStep, makeGame } from '../../lib/games'
import { GameRow } from '../playroom/GameRow'
import { useStepStanding } from '../playroom/usePath'
import { formatDuration } from '../../lib/dates'
import { domainColor } from '../../lib/rubric'
import { setBlockStatus } from '../../lib/planEdit'
import { BlockCheck } from './BlockCheck'

/**
 * One activity from the plan, read in the order a parent needs it: what the
 * activity is, what you do, and why. Closed, the steps show in a few lines;
 * open, the whole card stands on its own, with the sign it is working and
 * what to have in hand. Nothing here assumes the plan is open elsewhere.
 *
 * `domain` is a free string from the plan artifact, so the accent colour falls
 * back gracefully when the agent invents a domain we have no hue for.
 */
export function BlockCard({ block, date, checkable = true }: { block: PlanBlock; date: string; checkable?: boolean }) {
  const [open, setOpen] = useState(false)
  // Linked games and the ones made for this activity and not linked yet,
  // so a game being made shows the moment it is asked for.
  const games = useAuroraStore((s: any) => materialsForBlock(s.artifacts, block)) as any[]
  const author = useAuroraStore((s: any) => authorName(s, block.authorId))
  const childId = useAuroraStore((s: any) => s.ui.activeChildId)
  const making = games.some((g) => gameStatus(g) === 'making')
  // One filled button per card: a game ready to play is the next thing to
  // do, so its Play leads; otherwise logging how it went does.
  const step = useStepStanding(games.find(isPathStep))
  const playable = games.some((g) => gameStatus(g) === 'ready' && !(isPathStep(g) && step?.state === 'locked'))
  // A built-in book or set is the activity's own thing to play, not a start
  // for more made games, so its card offers no "Make another".
  const builtIn = games.some(isPathStep)

  const settled = block.status && block.status !== 'pending'

  // Carrying the block through is what lets the capture land against this
  // activity rather than the day in general.
  const logIt = () => {
    const s = auroraStore.getState() as any
    s.startRecording(block.id)
  }

  const ask = (text: string) => {
    const s = auroraStore.getState() as any
    s.setComposing(text)
    s.setView('thread')
  }

  return (
    <article
      className={`block${settled ? ' is-settled' : ''}${open ? ' is-open' : ''}`}
      style={{ '--domain-color': domainColor(block.domain) } as React.CSSProperties}
    >
      <span className="block-dot-hit">
        <button
          type="button"
          className={`block-dot${settled ? ' is-settled' : ''}`}
          onClick={() =>
            setBlockStatus(date, block.id, block.status === 'done' ? 'pending' : 'done', block.title)
          }
          aria-label={block.status === 'done' ? `Reopen ${block.title}` : `Mark ${block.title} done`}
          aria-pressed={block.status === 'done'}
        >
          {block.status === 'done' && <CheckIcon />}
          {block.status === 'partial' && <HalfIcon />}
          {block.status === 'skipped' && <SkipIcon />}
        </button>
      </span>
      <button className="block-head" onClick={() => setOpen((v) => !v)} aria-expanded={open}>

        <span className="block-head-text">
          <span className="block-meta">
            {block.domain && (
              <span className="pill pill-domain">{block.domainName ?? humanizeDomain(block.domain)}</span>
            )}
            <span className="block-time tnum">{formatDuration(block.minutes ?? 10)}</span>
            {games.length > 0 && (
              <span className={`block-game-tag${making ? ' is-making' : ''}`}>
                <SparkleIcon /> {making ? 'Making a game' : countThings(games)}
              </span>
            )}
          </span>
          <span className="block-title">{block.title}</span>
          {block.instructions && <span className="block-instructions">{block.instructions}</span>}
        </span>

        <span className="block-chevron" aria-hidden="true"><ChevronIcon /></span>
      </button>

      {open && (
        <div className="block-body">
          {!!(block.why || block.watchFor || block.materials?.length) && (
            <dl className="block-facts">
              {block.why && (
                <div className="block-fact">
                  <dt>Why</dt>
                  <dd>{block.why}</dd>
                </div>
              )}
              {block.watchFor && (
                <div className="block-fact">
                  <dt>Watch for</dt>
                  <dd>{block.watchFor}</dd>
                </div>
              )}
              {!!block.materials?.length && (
                <div className="block-fact">
                  <dt>Have ready</dt>
                  <dd>
                    <ul className="block-have">
                      {block.materials.map((m) => <li key={m}>{m}</li>)}
                    </ul>
                  </dd>
                </div>
              )}
            </dl>
          )}

          {settled && author && <p className="block-by">{markLabel(block.status, author)}</p>}

          {games.length > 0 && (
            <div className="block-games">
              {games.map((g) => <GameRow key={g._id} artifact={g} blockRef={block.id} />)}
            </div>
          )}

          <div className="block-actions">
            <button className={`btn ${playable ? 'btn-ghost' : 'btn-primary'} btn-sm`} onClick={logIt}>
              <MicIcon /> Log it
            </button>
            {!builtIn && <button
              className={`btn ${games.length ? 'btn-quiet' : 'btn-ghost'} btn-sm`}
              disabled={making || !childId}
              onClick={() => makeGame(childId, `Make something playable for "${block.title}"`, block.id)}
            >
              <SparkleIcon /> {making ? 'Making it…' : games.length ? 'Make another' : 'Make it playable'}
            </button>}
            <button
              className="btn btn-quiet btn-sm"
              onClick={() => ask(`Swap out "${block.title}" on ${date}. She's not into it today.`)}
            >
              Swap it
            </button>
          </div>
        </div>
      )}

      {checkable && <BlockCheck block={block} date={date} />}
    </article>
  )
}


/** "grossMotor" reads as "Gross Motor" when the plan carries no display name. */
function humanizeDomain(key: string): string {
  const words = key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/[_-]/g, ' ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}

const S = {
  viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2.25,
  strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, 'aria-hidden': true,
}
const CheckIcon = () => <svg {...S} width="12" height="12"><path d="M4 12.5l5 5L20 6.5" /></svg>
const SkipIcon = () => <svg {...S} width="11" height="11"><path d="M6 6l12 12M18 6L6 18" /></svg>
const HalfIcon = () => (
  <svg {...S} width="12" height="12" strokeWidth={2}>
    <path d="M12 4a8 8 0 0 1 0 16z" fill="currentColor" stroke="none" />
  </svg>
)
const ChevronIcon = () => <svg {...S} width="16" height="16" strokeWidth={2}><path d="M6 9l6 6 6-6" /></svg>
const MicIcon = () => (
  <svg {...S} width="14" height="14" strokeWidth={2}>
    <rect x="9" y="2" width="6" height="12" rx="3" />
    <path d="M5 11a7 7 0 0 0 14 0M12 18v4" />
  </svg>
)
const SparkleIcon = () => (
  <svg {...S} width="13" height="13" strokeWidth={2}>
    <path d="M12 4l1.5 4L18 9.5 13.5 11 12 15l-1.5-4L6 9.5 10.5 8z" />
  </svg>
)
